import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, voidRecord } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, PageHeader, StatusBadge, Modal, Field, inputCls, PrimaryButton, EmptyState } from "@/components/lims";
import SampleQrButton from "@/components/SampleQr";
import SigningFields, { useSigning } from "@/components/SigningFields";
import { Plus, Flask, Trash, PencilSimple, UserPlus, ArrowsClockwise, NotePencil } from "@phosphor-icons/react";
import { canDeleteTests, canAssignTests, canEnterResults, canReleaseResults } from "@/lib/roles";

// UX-04: this list matches the server state machine exactly — including the
// terminal Released state, which the prototype's filter omitted.
const TEST_STATUSES = ["Ordered", "In Progress", "Submitted", "Rejected", "Reviewed", "Released"];
const EDITABLE = ["Ordered", "In Progress", "Rejected"];

export default function Tests() {
  const { user } = useAuth();
  const canManage = canDeleteTests(user);
  const canAssign = canAssignTests(user);
  const canEdit = canEnterResults(user);
  const [tests, setTests] = useState([]);
  const [samples, setSamples] = useState([]);
  const [catalogue, setCatalogue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [assigneeId, setAssigneeId] = useState("");
  const [amendTarget, setAmendTarget] = useState(null);
  const [amendForm, setAmendForm] = useState({});
  const amendmentSigning = useSigning();
  const canAmend = canReleaseResults(user);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/tests");
    setTests(data);
    setLoading(false);
  }, []);
  const loadRefs = useCallback(async () => {
    if (!canAssign) return;
    const [s, c] = await Promise.all([api.get("/samples"), api.get("/catalogue?active_only=true")]);
    setSamples(s.data); setCatalogue(c.data);
  }, [canAssign]);
  useEffect(() => { load(); loadRefs(); }, [load, loadRefs]);

  const openNew = () => {
    setEditing(null);
    setForm({ sample_id: "", assay_id: "", assay_name: "" });
    setOpen(true);
  };
  const openEdit = (t) => { setEditing(t); setForm({ result_value: t.result_value || "", result_unit: t.result_unit || "", result_flag: t.result_flag || "", notes: t.notes || "" }); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = editing
      ? { ...form }
      : {
          sample_id: form.sample_id,
          assay_id: form.assay_id,
          assay_name: form.assay_name,
        };
    if (!editing && catalogue.length > 0) {
      const cat = catalogue.find((c) => c.id === form.assay_id);
      payload.assay_name = cat ? cat.name : form.assay_name;
    }
    try {
      if (editing) {
        await api.put(`/tests/${editing.id}`, payload);
        toast.success("Test updated");
      } else {
        await api.post("/tests", payload);
        toast.success("Test ordered");
      }
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const openAssign = async (t) => {
    setAssignTarget(t);
    setAssigneeId(t.assigned_to_id || "");
    try {
      const { data } = await api.get("/tests/assignees");
      setAssignees(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const doAssign = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/tests/${assignTarget.id}/assign`, { user_id: assigneeId || null });
      toast.success(assigneeId ? "Test assigned" : "Test unassigned");
      setAssignTarget(null); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const doRetest = async (t) => {
    if (!window.confirm(`Create a linked retest of ${t.test_code}?`)) return;
    try {
      const { data } = await api.post(`/tests/${t.id}/retest`);
      toast.success(`Retest ${data.test_code} created`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (t) => {
    if (!(await voidRecord(`/tests/${t.id}`, t.test_code))) return;
    toast.success("Test voided"); load();
  };

  const openAmend = (t) => {
    setAmendTarget(t);
    setAmendForm({ result_value: t.result_value || "", result_unit: t.result_unit || "", notes: t.notes || "", reason: "" });
    amendmentSigning.reset();
  };

  const doAmend = async (e) => {
    e.preventDefault();
    if (!amendForm.reason.trim()) { toast.error("An amendment reason is required"); return; }
    if (!amendmentSigning.password) { toast.error("Your password is required to sign this amendment"); return; }
    try {
      await api.post(`/tests/${amendTarget.id}/amend`, {
        reason: amendForm.reason.trim(),
        result_value: amendForm.result_value !== amendTarget.result_value ? amendForm.result_value : null,
        result_unit: amendForm.result_unit !== amendTarget.result_unit ? amendForm.result_unit : null,
        notes: amendForm.notes !== (amendTarget.notes || "") ? amendForm.notes : null,
        signing: amendmentSigning.block(),
      });
      toast.success(`${amendTarget.test_code} amended`);
      amendmentSigning.reset(); setAmendTarget(null); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const filtered = statusFilter ? tests.filter((test) => test.status === statusFilter) : tests;

  return (
    <Page>
      <PageHeader
        title="Tests & Results"
        subtitle="Order assays and track them through entry, review and release"
        action={canAssign ? <PrimaryButton data-testid="new-test-button" onClick={openNew}><Plus size={18} /> Order Test</PrimaryButton> : null}
      />

      <div className="flex gap-3 mb-4">
        <select aria-label="Filter tests by status" data-testid="test-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={`${inputCls} max-w-[200px]`}>
          <option value="">All statuses</option>
          {TEST_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        {loading ? (
          <div className="p-8 space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}</div>
        ) : filtered.length === 0 ? (
          <EmptyState icon={Flask} title="No tests found" subtitle="Tests are created at sample registration or ordered here" />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-semibold">Code</th>
                <th className="px-4 py-3 font-semibold">Assay</th>
                <th className="px-4 py-3 font-semibold">Sample</th>
                <th className="px-4 py-3 font-semibold">Assignee</th>
                <th className="px-4 py-3 font-semibold">Result</th>
                <th className="px-4 py-3 font-semibold">Flag</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} data-testid={`test-row-${t.test_code}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{t.test_code}{t.retest_of_code && <div className="text-[10px] text-status-warn">retest of {t.retest_of_code}</div>}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{t.assay_name}{t.method && <div className="text-xs text-slate-400 font-normal">{t.method}</div>}</td>
                  <td className="px-4 py-3 font-mono text-xs">
                    <div className="flex items-center gap-1">
                      <Link to={`/samples/${t.sample_id}`} className="text-slate-500 hover:text-slate-900 hover:underline">{t.sample_code}</Link>
                      {t.sample_id && <SampleQrButton sampleId={t.sample_id} sampleCode={t.sample_code} />}
                    </div>
                    <div className="font-sans text-slate-500">{t.client_name || ""}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{t.assigned_to || <span className="text-slate-300">unassigned</span>}</td>
                  <td className="px-4 py-3 text-slate-600">{t.result_value ? `${t.result_value} ${t.result_unit || ""}` : "—"}
                    {(t.spec_min != null || t.spec_max != null) && <div className="text-[10px] font-mono text-slate-400">spec {t.spec_min ?? ""}–{t.spec_max ?? ""}</div>}
                  </td>
                  <td className="px-4 py-3">{t.result_flag ? <StatusBadge status={t.result_flag} /> : <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={t.status} />
                    {t.released_by && <div className="text-[10px] text-slate-400 mt-0.5">by {t.released_by}</div>}
                    {t.amended && (
                      <div data-testid={`amended-${t.test_code}`} title={t.amend_reason} className="text-[10px] font-semibold text-status-warn mt-0.5">
                        AMENDED v{t.version}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {canAssign && EDITABLE.includes(t.status) && (
                        <button data-testid={`assign-test-${t.test_code}`} title="Assign to analyst" onClick={() => openAssign(t)} className="p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"><UserPlus size={16} /></button>
                      )}
                      {canAssign && (t.result_flag === "Abnormal" || t.result_flag === "Critical" || t.status === "Rejected") && (
                        <button data-testid={`retest-${t.test_code}`} title="Create linked retest" onClick={() => doRetest(t)} className="p-1.5 rounded-md text-slate-400 hover:text-status-warn hover:bg-status-warn/10 transition-colors"><ArrowsClockwise size={16} /></button>
                      )}
                      {canEdit && EDITABLE.includes(t.status) && (
                        <button data-testid={`edit-test-${t.test_code}`} onClick={() => openEdit(t)} className="p-1.5 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors"><PencilSimple size={16} /></button>
                      )}
                      {canAmend && t.status === "Released" && (
                        <button data-testid={`amend-test-${t.test_code}`} title="Amend released result (audited)" onClick={() => openAmend(t)} className="p-1.5 rounded-md text-slate-400 hover:text-status-warn hover:bg-status-warn/10 transition-colors"><NotePencil size={16} /></button>
                      )}
                      {canManage && <button data-testid={`delete-test-${t.test_code}`} onClick={() => remove(t)} className="p-1.5 rounded-md text-slate-400 hover:text-status-critical hover:bg-status-critical/10 transition-colors"><Trash size={16} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? `Update ${editing.test_code}` : "Order Test"} testid="test-modal">
        <form onSubmit={submit} className="space-y-4">
          {!editing && (
            <>
              <Field label="Sample">
                <select data-testid="test-sample-select" required value={form.sample_id} onChange={set("sample_id")} className={inputCls}>
                  <option value="">Select sample…</option>
                  {samples.map((s) => <option key={s.id} value={s.id}>{s.sample_code} · {s.name}</option>)}
                </select>
              </Field>
              <Field label="Assay">
                <select data-testid="test-assay-select" required value={form.assay_id} onChange={set("assay_id")} className={inputCls}>
                  <option value="">Select assay…</option>
                  {catalogue.map((c) => <option key={c.id} value={c.id}>{c.name}{c.unit ? ` (${c.unit})` : ""}</option>)}
                </select>
              </Field>
            </>
          )}
          {editing && (
            <>
              <div className="text-sm text-slate-500">
                Status: <StatusBadge status={editing.status} />
                <span className="ml-2 text-xs text-slate-400">moves through submit / review / release actions</span>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Result"><input data-testid="test-result-input" value={form.result_value || ""} onChange={set("result_value")} className={inputCls} /></Field>
                <Field label="Unit"><input value={form.result_unit || ""} onChange={set("result_unit")} className={inputCls} /></Field>
                <Field label="Flag"><select value={form.result_flag || ""} onChange={set("result_flag")} className={inputCls}>{["", "Normal", "Abnormal", "Critical"].map((f) => <option key={f} value={f}>{f || "auto"}</option>)}</select></Field>
              </div>
            </>
          )}
          {editing && (
            <Field label="Notes"><textarea value={form.notes || ""} onChange={set("notes")} rows={2} className={inputCls} /></Field>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
            <PrimaryButton data-testid="save-test-button" disabled={saving}>{editing ? "Save" : "Order"}</PrimaryButton>
          </div>
        </form>
      </Modal>

      <Modal open={!!amendTarget} onClose={() => { amendmentSigning.reset(); setAmendTarget(null); }} title={amendTarget ? `Amend ${amendTarget.test_code} (released)` : ""} testid="amend-modal">
        {amendTarget && (
          <form onSubmit={doAmend} className="space-y-4">
            <div className="rounded-md bg-status-warn/10 border border-status-warn/30 p-3 text-sm text-status-warn">
              Amending creates a new audited version; the current values are preserved as v{amendTarget.version || 1}.
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Result"><input data-testid="amend-result-input" value={amendForm.result_value} onChange={(e) => setAmendForm({ ...amendForm, result_value: e.target.value })} className={inputCls} /></Field>
              <Field label="Unit"><input value={amendForm.result_unit} onChange={(e) => setAmendForm({ ...amendForm, result_unit: e.target.value })} className={inputCls} /></Field>
            </div>
            <Field label="Notes"><textarea value={amendForm.notes} onChange={(e) => setAmendForm({ ...amendForm, notes: e.target.value })} rows={2} className={inputCls} /></Field>
            <Field label="Amendment reason (required)">
              <textarea data-testid="amend-reason-input" value={amendForm.reason} onChange={(e) => setAmendForm({ ...amendForm, reason: e.target.value })} rows={2} className={inputCls} />
            </Field>
            <SigningFields
              signing={amendmentSigning}
              meaning="Release amended result"
            />
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => { amendmentSigning.reset(); setAmendTarget(null); }} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
              <PrimaryButton data-testid="confirm-amend-button">Amend result</PrimaryButton>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={!!assignTarget} onClose={() => setAssignTarget(null)} title={assignTarget ? `Assign ${assignTarget.test_code}` : ""} testid="assign-modal">
        {assignTarget && (
          <form onSubmit={doAssign} className="space-y-4">
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600">
              {assignTarget.assay_name} · {assignTarget.sample_code}
              {assignTarget.assigned_to && <div className="text-xs text-slate-400 mt-0.5">currently assigned to {assignTarget.assigned_to}</div>}
            </div>
            <Field label="Analyst (open workload shown)">
              <select data-testid="assignee-select" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} className={inputCls}>
                <option value="">Unassigned</option>
                {assignees.map((a) => (
                  <option key={a.user_id} value={a.user_id}>{a.name} ({a.open} open)</option>
                ))}
              </select>
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setAssignTarget(null)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
              <PrimaryButton data-testid="confirm-assign-button">Assign</PrimaryButton>
            </div>
          </form>
        )}
      </Modal>
    </Page>
  );
}
